import { useQuery } from "@tanstack/react-query";
import { toBlob } from "html-to-image";
import { Copy, Download, Megaphone, Share2 } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { monthStart } from "../callPlan/callPlan";
import { useRanking } from "../callPlan/useCallPlan";
import { BriefCard, CARD_HEIGHT, CARD_WIDTH } from "./BriefCard";
import { markBriefSent } from "./briefSent";
import {
  BRIEF_PATH,
  briefCaption,
  buildBrief,
  type BriefLead,
} from "./dailyBrief";

const useBrief = () => {
  const leads = useQuery({
    queryKey: ["gula-brief-leads"],
    meta: { persist: false },
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_clients_summary")
        .select("stage, sales_id, lead_temperature, recontact_at, created_at")
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as BriefLead[];
    },
  });
  const ranking = useRanking(monthStart());
  const brief = useMemo(
    () =>
      leads.data && ranking.data
        ? buildBrief({ leads: leads.data, ranking: ranking.data })
        : null,
    [leads.data, ranking.data],
  );
  return brief;
};

/** Width available for the preview, so the 1080 px card fits the screen. */
const useFitScale = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.4);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () =>
      setScale(Math.min(0.6, element.clientWidth / CARD_WIDTH));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, scale };
};

/**
 * "Parte del día": the morning card for the setters' WhatsApp group. WhatsApp
 * does not let the CRM post in a group, so an admin shares it in two taps.
 */
export const BriefPage = () => {
  const brief = useBrief();
  const notify = useNotify();
  const cardRef = useRef<HTMLDivElement>(null);
  const { ref: fitRef, scale } = useFitScale();
  const [busy, setBusy] = useState(false);

  const picture = async () => {
    if (!cardRef.current) throw new Error("El parte aún no está listo");
    const blob = await toBlob(cardRef.current, {
      width: CARD_WIDTH,
      height: CARD_HEIGHT,
      pixelRatio: 1,
      cacheBust: true,
    });
    if (!blob) throw new Error("No se ha podido crear la imagen");
    return new File([blob], `parte-${brief?.day ?? "hoy"}.png`, {
      type: "image/png",
    });
  };

  const copyCaption = async () => {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(briefCaption(brief));
      notify("Texto copiado: pégalo en el grupo", { type: "success" });
    } catch {
      notify("No se ha podido copiar el texto", { type: "error" });
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const file = await picture();
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(url);
      markBriefSent();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    if (!brief) return;
    setBusy(true);
    try {
      const file = await picture();
      const data = { files: [file], text: briefCaption(brief) };
      if (navigator.canShare?.(data)) {
        await navigator.share(data);
        markBriefSent();
      } else {
        // Computers without sharing: download the picture and copy the text
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        link.click();
        URL.revokeObjectURL(url);
        await navigator.clipboard.writeText(briefCaption(brief));
        markBriefSent();
        notify(
          "Imagen descargada y texto copiado: arrástrala al grupo de WhatsApp y pega el texto",
          { type: "info" },
        );
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 py-4">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
          <Megaphone className="h-7 w-7" />
          Parte del día
        </h1>
        <p className="text-sm text-muted-foreground">
          Para el grupo de WhatsApp de los setters, cada mañana a las 9:00. Los
          datos son de este momento.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          className="h-12 flex-1 rounded-2xl bg-[#25D366] text-base text-neutral-900 hover:bg-[#1ebe5a]"
          disabled={!brief || busy}
          onClick={share}
        >
          <Share2 className="h-5 w-5" />
          Compartir al grupo
        </Button>
        <Button
          variant="outline"
          className="h-12 rounded-2xl"
          disabled={!brief || busy}
          onClick={download}
        >
          <Download className="h-4 w-4" />
          Imagen
        </Button>
        <Button
          variant="outline"
          className="h-12 rounded-2xl"
          disabled={!brief}
          onClick={copyCaption}
        >
          <Copy className="h-4 w-4" />
          Texto
        </Button>
      </div>

      <div ref={fitRef} className="w-full">
        {brief ? (
          <div
            className="overflow-hidden rounded-2xl shadow-lg"
            style={{ width: CARD_WIDTH * scale, height: CARD_HEIGHT * scale }}
          >
            <div
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
            >
              <BriefCard ref={cardRef} brief={brief} />
            </div>
          </div>
        ) : (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Preparando el parte…
          </p>
        )}
      </div>

      {brief ? (
        <pre className="whitespace-pre-wrap rounded-2xl border bg-muted/40 p-4 text-sm">
          {briefCaption(brief)}
        </pre>
      ) : null}
    </div>
  );
};

BriefPage.path = BRIEF_PATH;
